import { saveAs } from 'file-saver';
import { mapActions, mapGetters, mapMutations } from 'vuex';
import { ToastProgrammatic as Toast } from 'buefy';

import CanceledMemberTable from '@/components/Order/CanceledMemberTable/CanceledMemberTable.vue';
import CopyLinkModal from '@/components/Order/CopyLinkModal/CopyLinkModal.vue';
import MemberTable from '@/components/Order/MemberTable/MemberTable.vue';
import OrderItemTable from '@/components/Order/OrderItemTable/OrderItemTable.vue';
import OrderMemberTable from '@/components/Order/OrderMemberTable/OrderMemberTable.vue';

import { downloadOrderItemsReport, downloadOrderMembersReport, downloadOrdersReport } from '@/api/reports';
import { GET_ORDER, SET_DEPARTMENT_LIST } from '@/store/orders/constants';
import { GET_DEPARTMENT_LIST } from '@/store/setting/constants';
import { GET_ORDER_METHOD_MAPPING } from '@/store/store/constants';
import { ORDER_STATUS, SNACKBAR_TYPES } from '@/utils/constants';
import bus from '@/utils/bus';
import { formatPrice } from '@/utils/helper';

const OVERVIEW_TABS_VALUE = {
  ORDERED_ITEMS: 'ordered_items',
  ORDERED_MEMBERS: 'ordered_members',
  NOT_ORDER_MEMBERS: 'not_order_members',
  CANCELLED_ORDERS: 'cancelled_orders',
};

export default {
  name: 'OrderOverview',
  components: {
    CanceledMemberTable,
    CopyLinkModal,
    MemberTable,
    OrderItemTable,
    OrderMemberTable,
  },
  props: {
    orderId: {
      type: Number,
      required: true,
    },
  },
  data() {
    return {
      activeTab: OVERVIEW_TABS_VALUE.ORDERED_ITEMS,
      exportBtnLoading: false,
      exportOrderMembersBtnLoading: false,
      filter: undefined,
      isCopyActive: false,
      isFiltered: false,
      keyword: '',
      loading: false,
      loadingDepartments: false,
      orderInfo: {},
      orderMembers: [],
      orders: [],
      showOrderDetail: true,
      showUrlModal: false,
    };
  },
  watch: {
    activeTab(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.isFiltered = false;
        this.keyword = '';
      }
    },
  },
  computed: {
    canceledOrders() {
      if (this.isFiltered) {
        return (this.orderInfo?.canceled_orders || []).filter((item) => this.filterList(item));
      }
      return this.orderInfo?.canceled_orders || [];
    },
    unorderedMembers() {
      if (this.isFiltered) {
        return (this.orderInfo?.unordered_members || [])
          .filter(({ member_detail: item = {} }) => this.filterList(item));
      }
      return this.orderInfo?.unordered_members || [];
    },
    isTabCancelledOrders() {
      return this.activeTab === OVERVIEW_TABS_VALUE.CANCELLED_ORDERS;
    },
    isTabNotOrderMembers() {
      return this.activeTab === OVERVIEW_TABS_VALUE.NOT_ORDER_MEMBERS;
    },
    isTabOrderedItems() {
      return this.activeTab === OVERVIEW_TABS_VALUE.ORDERED_ITEMS;
    },
    isTabOrderedMembers() {
      return this.activeTab === OVERVIEW_TABS_VALUE.ORDERED_MEMBERS;
    },
    orderStatistic() {
      const {
        canceled_count: canceledMemberCount = '--',
        received,
        total,
        total_item_count: itemCount,
        ordered_member_count: orderedMemberCount,
        unordered_member_count: unorderedMemberCount,
        unreceived,
      } = this.orderInfo;
      return {
        canceledMemberCount,
        total,
        itemCount:
          itemCount || itemCount === 0 ? itemCount : '--',
        orderedMemberCount:
          orderedMemberCount || orderedMemberCount === 0 ? orderedMemberCount : '--',
        received: received || received === 0 ? received : '--',
        unorderedMemberCount:
          unorderedMemberCount || unorderedMemberCount === 0 ? unorderedMemberCount : '--',
        unreceived: unreceived || unreceived === 0 ? unreceived : '--',
      };
    },
    orderedItems() {
      return this.orderInfo?.ordered_items || [];
    },
    orderedMembers: {
      get() {
        if (this.isFiltered) {
          return (this.orderInfo?.ordered_members || []).filter((item) => this.filterList(item));
        }
        return this.orderInfo?.ordered_members || [];
      },
      set(newValue) {
        this.orderInfo.ordered_members = newValue;
      },
    },
    orderStatus() {
      return this.orderInfo?.status;
    },
    orderHasExpired() {
      return this.orderStatus === ORDER_STATUS.ENDED;
    },
    showReceiveInfo() {
      return this.orderStatus !== ORDER_STATUS.SCHEDULED;
    },
    storePhone() {
      return this.orderInfo?.store_order_way?.telephone
        || this.orderInfo?.store_order_way?.phone;
    },
    tabs() {
      return [
        {
          label: '已訂項目',
          total: this.orderStatistic?.itemCount || 0,
          value: OVERVIEW_TABS_VALUE.ORDERED_ITEMS,
        },
        {
          label: '已訂人員',
          total: this.orderStatistic?.orderedMemberCount || 0,
          value: OVERVIEW_TABS_VALUE.ORDERED_MEMBERS,
        },
        {
          label: '未訂人員',
          total: this.orderStatistic?.unorderedMemberCount || 0,
          value: OVERVIEW_TABS_VALUE.NOT_ORDER_MEMBERS,
        },
        {
          label: '已取消訂單',
          total: this.orderStatistic?.canceledMemberCount || 0,
          value: OVERVIEW_TABS_VALUE.CANCELLED_ORDERS,
        },
      ];
    },
    availableOrderMethods() {
      const excludedMethods = ['phone', 'telephone'];
      const orderMethod = this.orderInfo?.store_order_way || {};
      const availableMethods = this.orderMethods
        .filter((i) => !excludedMethods.includes(i.key) && !!orderMethod[i.key]);
      const lineMethodIndex = availableMethods.findIndex((m) => m.key === 'line_url');

      /* If `line_url` start with `@` then transform to link to account page */
      if (lineMethodIndex >= 0 && /^@/.test(orderMethod.line_url)) {
        availableMethods[lineMethodIndex].linkTitle = orderMethod.line_url;
        orderMethod.line_url = `https://page.line.me/?accountId=${orderMethod.line_url.replace(/^@/, '')}`;
      }
      return availableMethods;
    },
    shareLinkVisibility() {
      return !(
        this.loading
          || [ORDER_STATUS.COMPLETED, ORDER_STATUS.CANCELLED].includes(this.orderInfo.status)
      );
    },
    ...mapGetters('store', {
      orderMethods: GET_ORDER_METHOD_MAPPING,
    }),
    ...mapGetters(['isProduction']),
  },
  created() {
    bus.$on('GET_ORDER_INFOS', async () => {
      this.orderInfo = await this.getOrder(this.orderId);
    });
  },
  async mounted() {
    try {
      if (!this.orderInfo) {
        return;
      }
      this.loading = true;
      this.orderInfo = await this.getOrder(this.orderId);
      const departments = await this.getDepartmentList();
      this.setDepartmentList(departments);
      if (this.$route.query.created) {
        this.showUrlModal = true;
        this.$router.replace(this.$route.path);
      }
    } catch (ex) {
      this.onError(ex);
    } finally {
      this.loading = false;
    }
  },
  methods: {
    ...mapActions('setting', {
      getDepartmentList: GET_DEPARTMENT_LIST,
    }),
    ...mapMutations('orders', {
      setDepartmentList: SET_DEPARTMENT_LIST,
    }),
    async exportStatisticsReport() {
      try {
        this.exportBtnLoading = true;
        const res = await downloadOrdersReport(this.orderId);
        const { filename, content = '' } = res || {};
        const blob = new Blob([content], { type: 'text/csv' });

        saveAs(blob, filename);
      } catch (ex) {
        this.$snackbar({
          type: SNACKBAR_TYPES.ERROR,
          title: '下載失敗',
          msg: ex.message || '下載時發生未知錯誤，請再試一次',
        });
      } finally {
        this.exportBtnLoading = false;
      }
    },
    async exportOrdersReport(byMember = true) {
      try {
        this.exportOrderMembersBtnLoading = true;
        let res = {};
        if (byMember) {
          res = await downloadOrderMembersReport(this.orderId);
        } else {
          res = await downloadOrderItemsReport(this.orderId);
        }
        const { filename, content = '' } = res || {};
        const blob = new Blob([content], { type: 'text/csv' });

        saveAs(blob, filename);
      } catch (ex) {
        this.$snackbar({
          type: SNACKBAR_TYPES.ERROR,
          title: '下載失敗',
          msg: ex.message || '下載時發生未知錯誤，請再試一次',
        });
      } finally {
        this.exportOrderMembersBtnLoading = false;
      }
    },
    async onSearch() {
      if (!this.filter) {
        this.onReset();
        return;
      }
      this.isFiltered = true;
      this.keyword = this.filter;
    },
    async onReset() {
      this.isFiltered = false;
      this.filter = '';
      this.keyword = '';
    },

    formatPrice,
    getOrderDetails(items = []) {
      return items?.map((item = {}) => {
        const {
          entree: {
            name,
            options = [],
          } = {},
          ...props
        } = item || [];

        return {
          name,
          options,
          ...props,
        };
      }) || [];
    },
    toggleOrderDetail() {
      this.showOrderDetail = !this.showOrderDetail;
    },
    getOrderMethodImage(filename) {
      const images = require.context('@/assets/images', false, /\.svg$/);
      return images(`./${filename}`);
    },
    filterList(item) {
      const lowerKeyword = `${this.keyword}`.toLowerCase();
      return item?.name?.toLowerCase().includes(lowerKeyword)
      || item?.serial?.toLowerCase().includes(lowerKeyword)
      || item?.account?.toLowerCase().includes(lowerKeyword);
    },
    onError(error) {
      const { message = '' } = error;

      Toast.open({
        message: `讀取訂單資訊時發生錯誤，請稍後再試${message}`,
        type: 'is-danger',
      });
    },
    ...mapActions('orders', {
      getOrder: GET_ORDER,
    }),
  },
};
